import {CommandCaller} from "../../lib/commands";
import {dateToYYYYMMDD, Dispatcher, mapValues, maybeToInts, secsToHMS, sumReducer, zipObj} from "../../lib/utils";
import {setCookie} from "../cookie";
import {DashboardAction} from "./action";
import {PageCallbackProps, PageValueProps} from "../../lib/sbhtml/fixed-data-grid";
import {Store} from "redux";
import {VisitsColors, SourceColors} from "./presentation";

type DashboardLoadingStatus = "loading"|"done"|"failed"

export type DashboardState = {
	activeTab: keyof AllTabData,
	loading: DashboardLoadingStatus
	fromDate: Date
	toDate: Date
	includesNewVisitors: boolean
	includesReturningVisitors: boolean
	visits: DashboardVisitorsEntry[]|null /* null means it aborted, not merely it's never obtained data*/
	times: DashboardTimesEntry[]|null /* null means it aborted, not merely it's never obtained data*/
	sources: DashboardSourcesEntry<number>|null
	overviewing: number|undefined
	keywords: {[index: number]: Keywords}
	tabs: AllTabData
}

export type DashboardVisitorsEntry = {
	LongDate: string,
	PopupDate: string,
	ShortDate: string,
	date: string,
	newVisits: number
	pageviews: number
	serial: string,
	visits: number
}

export type DashboardTimesEntry = {
	visits: number,
	newVisits: number,
	timeTotal: number
}

export type DashboardSourcesEntry<Numeric extends number|string> = {
	organicTotal: Numeric|null
	referralTotal: Numeric|null
	noneTotal: Numeric|null
	otherTotal: Numeric|null
}

export type ValueProps = VisitsColors & SourceColors & {
	activeTab: Tab,
	loading: DashboardLoadingStatus
	fromDate: Date
	toDate: Date
	includesNewVisitors: boolean
	includesReturningVisitors: boolean
	sources: DashboardSourcesEntry<number>|null
	visits: number[]
	returningVisits: number[]
	newVisits: number[]
	pageviews: number[]
	totalVisits: number
	totalReturningVisits: number
	totalNewVisits: number
	totalPageviews: number
	averageTimeOnSite: number
	pagesPerVisit: number
	labels: string[]
	overviewing: undefined|DashboardVisitorsEntry
	keywords: Keywords
	tabs: AllTabData
	hasEventTracking: boolean
	hasEcommerceTracking: boolean
}

export type OwnProps = VisitsColors & SourceColors & {
	getDashboardDataURI: string,
	getKeywordsURI: string,
	hasEventTracking: boolean,
	hasEcommerceTracking: boolean,
	store: Store<DashboardState>
	cookieName: string
}

export type CallbackProps = {
	showDayById: (id: number) => void
	setNewVisitors: (newVisitors: boolean) => void
	setReturningVisitors: (newVisitors: boolean) => void
	setFromDate: (d: Date) => void
	setToDate: (d: Date) => void
	updateVisitsData: () => void
	onRequestCloseOverview: () => void
	paginationCallbacks: (on: Datagrid) => PageCallbackProps
	switchTab: (tab: Tab) => void
}

export type Datagrid = Tab

export type Tab = keyof AllTabData

export type AllTabData = {
	'traffic sources': TabData<SourceInfo, 'traffic sources'>
	'keywords': TabData<KeywordsDetail, 'keywords'>
	'content': TabData<ContentDetail, 'content'>
	'event detail': TabData<ContentDetail, 'event detail'>
	'ecommerce': TabData<EcommerceDetail, 'ecommerce'>
}

type TabData<T, Label extends string> = {
	pagination: PageValueProps<Label>,
	data: T[]|null
	typePlaceholder: T // this entity is never read. it is provided for type arithmetic only. I can't seem to extract the K in Array<K>
}

export type SourceInfo = {
	i: number
	source: string
	medium: string
	visits: number
	avgTimeOnSite: number
	highlight: boolean
}

export type EcommerceDetail = {
	i: number,
	productName: string,
	uniquePurchasesTotal: number,
	itemRevenueTotal: number,
}

export type ContentDetail = {
	i: number,
	url: string,
	maintitle: string,
	greytitle: string,
	pageviewsTotal: number|string,
	time: number,
	visitsTotal: string
}

export type KeywordsDetail = {
	i: number,
	highlight: boolean,
	keyword: string,
	visitsTotal: string,
	avgTime: number
}

export const mapStateToProps = (s: DashboardState, props: OwnProps): ValueProps & {times: any} => {
	const visits = s.visits || []
	const analytics = zipObj(visits, ['visits', 'newVisits', 'ShortDate', 'pageviews', ])
	const times = zipObj(s.times || [], ['visits', 'newVisits', 'timeTotal'])
	const returningVisits = visits.map(v => v.visits - v.newVisits)
	const totalTime = times.timeTotal.reduce(sumReducer, 0)
	// the original code has two ways of calculating the total visits. i have not proven if they are the same or different, but maintained it.
	const totalVisits1 = times.visits.reduce(sumReducer, 0);
	const totalVisits2 = analytics.visits.reduce(sumReducer, 0);
	const totalPageviews = analytics.pageviews.reduce(sumReducer, 0);

	return {
		...s,
		visits: analytics.visits,
		pageviews: analytics.pageviews,
		newVisits: analytics.newVisits,
		visitsColor: props.visitsColor,
		newVisitsColor: props.newVisitsColor,
		returningVisitsColor: props.returningVisitsColor,
		pageviewsColor: props.pageviewsColor,
		directColor: props.directColor,
		searchColor: props.searchColor,
		otherColor: props.otherColor,
		referringColor: props.referringColor,
		hasEventTracking: props.hasEventTracking,
		hasEcommerceTracking: props.hasEcommerceTracking,
		totalVisits: totalVisits2,
		totalNewVisits: analytics.newVisits.reduce(sumReducer, 0),
		totalPageviews,
		returningVisits,
		averageTimeOnSite: totalVisits1 ?  totalTime / totalVisits1 : 0,
		totalReturningVisits: returningVisits.reduce(sumReducer, 0),
		labels: analytics.ShortDate,
		pagesPerVisit: totalVisits2 ? totalPageviews / totalVisits2 : 0,
		overviewing: s.overviewing !== void 0 && s.visits ? s.visits[s.overviewing] : undefined,
		keywords: s.overviewing !== void 0 && s.keywords[s.overviewing] ? s.keywords[s.overviewing] : {type: "loading"}
	}
}

type KeywordsDataAjaxEntry = {bouncesTotal: string, keyword: string, timeTotal: string, uniquePageViewsTotal: string, visitsTotal: string}
type KeywordsDataAjax = { [index: number]: KeywordsDataAjaxEntry }

export type Keywords = HasValidKeywordsData | KeywordsDataDownloadError | {type: 'loading'}
export type HasValidKeywordsData = {type: "has result", result: ValidKeywordsData[]}
type KeywordsDataDownloadError = {type: "error", error: string, cause: string}

type ValidKeywordsData = {
	bouncesTotal: number,
	keyword: string,
	timeTotal: number,
	uniquePageViewsTotal: number,
	visitsTotal: number,
	averageTime: string
}

type DashboardDataJsonResponse = {
	visits: DashboardVisitorsResponseEntryAjax[],
	sources: DashboardSourcesEntry<string> | null,
	times: { visitsTotal: string, newVisitsTotal: string, timeTotal: string }[],
	sourcesDetail: SourceInfo[] | null,
	keywordsDetail: KeywordsDetail[] | null
	contentDetail: ContentDetail[] | null
	eventDetail: ContentDetail[] | null
	ecommerce: EcommerceDetail[] | null
}

type DashboardVisitorsResponseEntryAjax = {
	LongDate: string,
	PopupDate: string,
	ShortDate: string,
	date: string,
	newVisits: string /* stringy number*/,
	pageviews: string /*stringy number*/,
	serial: string,
	visits: string /* stringy number*/,
}

export const mapDispatchToProps = (dispatch: Dispatcher<DashboardAction, DashboardState>, props: OwnProps): CallbackProps => ({
	switchTab: (tab: Tab) => dispatch({dashboard: true, type: "switch tab", tab}),
	paginationCallbacks: (on: Datagrid) => ({
		setLimit: (limit: number | null) => dispatch({dashboard: true, type: "set limit", limit, on}),
		setPage:  (page: number) => dispatch({dashboard: true, type: "set page", page, on}),
	}),
	setNewVisitors: (to: boolean) => dispatch({dashboard: true, type: "set new visitors checked", to}),
	setReturningVisitors: (to: boolean) => dispatch({dashboard: true, type: "set returning visitors checked", to}),
	setFromDate: (date: Date) => dispatch({dashboard: true, type: "set from date", date}),
	setToDate: (date: Date) => dispatch({dashboard: true, type: "set to date", date}),
	onRequestCloseOverview: () => dispatch({dashboard: true, type: "close overview"}),
	showDayById: (dayId: number) => dispatch(async (dispatch_, getState) => {
		dispatch({dashboard: true, type: "open overview for day by id", dayId})

		const s = getState()
		if ((s.keywords[dayId] && s.keywords[dayId].type=="has result") || !s.visits || !s.visits[dayId]) return

		try {
			const result = await new CommandCaller(props.getKeywordsURI).runPost(
				'',
				{
					date: s.visits[dayId].date
				}
			) as KeywordsDataAjax
			const resultMapped: ValidKeywordsData[] = mapValues<KeywordsDataAjaxEntry, ValidKeywordsData, KeywordsDataAjax>(result, (a): ValidKeywordsData => {
				const timeTotal = Number.parseFloat(a.timeTotal)
				const visitsTotal = Number.parseInt(a.visitsTotal)

				return {
					bouncesTotal: Number.parseFloat(a.bouncesTotal),
					keyword: a.keyword,
					timeTotal,
					uniquePageViewsTotal: Number.parseInt(a.uniquePageViewsTotal),
					visitsTotal,
					averageTime: secsToHMS(timeTotal/visitsTotal),
				}
			})
			dispatch({dashboard: true, type: "add to keyword cache", dayId, result: {type: "has result", result: resultMapped} })
		} catch (e) {
			const result: Keywords = {
				type: "error",
				error: "Unexpected error",
				cause: "Loading the results"
			}
			dispatch({dashboard: true, type: "add to keyword cache", dayId, result})
			console.log(e)
		}

	}),
	updateVisitsData: () => dispatch(async (dispatch_, getState) => {
		dispatch({dashboard: true, type: "start obtaining dashboard data"})
		const state = getState()
		if (state.includesNewVisitors && state.includesReturningVisitors) {
			setCookie(props.cookieName, 'segments', '/', 30)
		} else if (state.includesNewVisitors) {
			setCookie(props.cookieName, 'newvisits', '/', 30)
		} else if (state.includesReturningVisitors) {
			setCookie(props.cookieName, 'returnvisits', '/', 30)
		} else {
			setCookie(props.cookieName, '', '/', 30)
		}

		try {
			const result = await new CommandCaller(props.getDashboardDataURI).runPost(
				'',
				{
					fromDate: dateToYYYYMMDD(state.fromDate),
					toDate: dateToYYYYMMDD(state.toDate),
				}
			) as DashboardDataJsonResponse
			dispatch({
				dashboard: true,
				type: "finished obtaining dashboard data",
				data: {
					ecommerce: result.ecommerce,
					eventDetail: result.eventDetail,
					contentDetail: result.contentDetail,
					sourcesDetail: result.sourcesDetail,
					keywordsDetail: result.keywordsDetail,
					times: result.times.map((e): DashboardTimesEntry => ({
						visits: Number.parseInt(e.visitsTotal),
						newVisits: Number.parseInt(e.newVisitsTotal),
						timeTotal: Number.parseInt(e.timeTotal),
					})),
					sources: result.sources && maybeToInts(result.sources),
					visits: result.visits.map((e): DashboardVisitorsEntry => ({
						...e,
						newVisits: Number.parseInt(e.newVisits),
						pageviews: Number.parseInt(e.pageviews),
						visits: Number.parseInt(e.visits),
					})),
				}
			})
		} catch (e) {
			dispatch({dashboard: true, type: "aborted obtaining dashboard data"})
			console.log(e)
		}
	})
})



// WEBPACK FOOTER //
// ./src/analytics/dashboard/app.ts